import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, TextField, InputAdornment, Box, Typography,
  List, ListItemButton, ListItemText, Chip, CircularProgress, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';

const TYPE_LABEL = {
  purchase_order: { label: 'PO', color: 'primary' },
  transfer: { label: 'Transfer', color: 'info' },
  supplier: { label: 'Supplier', color: 'success' },
  product: { label: 'SKU', color: 'default' },
  wholesale_order: { label: 'Order', color: 'warning' },
};

/**
 * Global Cmd+K search palette. Mounted once in Layout; listens for the
 * keyboard shortcut and renders a modal with cross-entity results.
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K (macOS) / Ctrl+K (Windows/Linux) toggles the palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Debounced fetch — 200ms after last keystroke. Minimum 2-char query so we
  // don't flood the server while the user is starting to type.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open || q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/api/search/global', { params: { q: q.trim() } });
        setResults(r.data?.results || []);
        setHighlightIdx(0);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setQ('');
      setResults([]);
      setHighlightIdx(0);
      // autoFocus on the dialog input — slight delay so MUI renders first.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = (item) => {
    setOpen(false);
    navigate(item.link);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[highlightIdx]) {
      e.preventDefault();
      handleSelect(results[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
      PaperProps={{ sx: { position: 'fixed', top: 80, m: 0 } }}>
      <DialogContent sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          placeholder="Search POs, orders, SKUs, suppliers, transfers…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
            sx: { fontSize: 18, py: 1 },
          }}
          variant="standard"
          sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        />

        {q.trim().length >= 2 && !loading && results.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2">No matches for "{q}"</Typography>
          </Box>
        )}

        {q.trim().length < 2 && (
          <Box sx={{ p: 2, color: 'text.disabled' }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Search across:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.values(TYPE_LABEL).map(t => (
                <Chip key={t.label} label={t.label} size="small" color={t.color} variant="outlined" />
              ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
              ↑↓ navigate · Enter open · Esc close
            </Typography>
          </Box>
        )}

        {results.length > 0 && (
          <List dense sx={{ maxHeight: 460, overflow: 'auto', py: 0 }}>
            {results.map((item, idx) => {
              const meta = TYPE_LABEL[item.type] || { label: item.type, color: 'default' };
              return (
                <React.Fragment key={`${item.type}-${item.link}-${idx}`}>
                  <ListItemButton
                    selected={idx === highlightIdx}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    sx={{ py: 1 }}
                  >
                    <Chip label={meta.label} size="small" color={meta.color}
                      sx={{ mr: 1.5, minWidth: 70, fontSize: 10, fontWeight: 700 }} />
                    <ListItemText
                      primary={item.label}
                      secondary={item.sublabel}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                      secondaryTypographyProps={{ fontSize: 11, sx: { fontFamily: 'monospace', color: 'text.secondary' } }}
                    />
                  </ListItemButton>
                  {idx < results.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
